import React, { useState, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { connect } from 'react-redux';
import Select from 'react-select';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Popover } from '@mui/material';

const NetZeroChart = ({ id, orgTargets, multiYearData, editing, branding }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const { palette } = branding;
  function formatTargetCoverage(arr) {
    if (!Array.isArray(arr)) return '';
    return arr
      .map(
        (item) =>
          item
            .replace(/(\d+)/, ' $1') // Add space before numbers
            .replace(/^./, (c) => c.toUpperCase()) // Capitalize first letter
      )
      .join(', ');
  }
  const targetOptions = orgTargets?.map((target) => ({
    value: target?.id,
    label: formatTargetCoverage(target?.targetCoverage),
  }));
  const [orgTarget, setOrgTarget] = useState(orgTargets?.[0]);
  const baseYearEmissions = useMemo(() => {
    return orgTarget?.settingMethod === 'Physical intensity convergence'
      ? orgTarget.baseYearEmissions / orgTarget.baseYearOutput
      : orgTarget.baseYearEmissions;
  }, [orgTarget]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  let currentEmissionsData = [];
  // if (!orgTarget?.useData) {
  currentEmissionsData =
    multiYearData &&
    Object.keys(multiYearData)
      ?.filter((year) => Number(year) >= Number(orgTarget?.baseYear))
      ?.map((year) => {
        const yearData = multiYearData[year];

        const filteredData = Object.keys(yearData)
          ?.filter((key) => orgTarget?.targetCoverage.includes(key))
          ?.reduce((sum, key) => {
            sum += yearData[key];
            return sum;
          }, 0);

        return filteredData;
      }, {});
  /*}  else {
    currentEmissionsData = Object.entries(multiYearData)
      .filter(([year]) => Number(year) >= Number(baseYear)) // Keep only years >= baseYear
      .map(([, item]) => item.baseYearEmissions);
  } */

  const netZero = orgTarget?.useData
    ? baseYearEmissions * (1 - orgTarget?.absoluteReduction / 100)
    : orgTarget?.settingMethod === 'Physical intensity convergence'
    ? (currentEmissionsData?.[0] / orgTarget?.baseYearOutput) * (1 - orgTarget?.absoluteReduction / 100) // For intensity targets, calculate net zero based on the first year's emissions
    : currentEmissionsData?.[0] * (1 - orgTarget?.absoluteReduction / 100); // Calculate the net zero target

  /* const predictEmissions = (data, startYear, endYear) => { //Function to predict future emissions using linear regression
    const initialYears = data.map((_, index) => startYear + index);
    const initialLength = data.length;

    // Calculate sum of X, Y, XY, and XX for linear regression
    const sumX = initialYears.reduce((acc, year) => acc + year, 0);
    const sumY = data.reduce((acc, value) => acc + value, 0);
    const sumXY = initialYears.reduce((acc, year, index) => acc + year * data[index], 0);
    const sumXX = initialYears.reduce((acc, year) => acc + year * year, 0);

    // Calculate slope (m) and intercept (b) for the linear equation y = mx + b
    const slope = (initialLength * sumXY - sumX * sumY) / (initialLength * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / initialLength;

    const predictedData = [...data]; // Start with the initial data points

    // Predict values for years beyond the initial data points
    for (let year = startYear + initialLength; year <= endYear; year++) {
      const predictedValue = slope * year + intercept;
      predictedData.push(predictedValue);
    }

    return predictedData;
  }; */

  function calculateDataPoints(currentData, startYear, endYear) {
    //if (orgTarget?.useData) {
    const totalYears = endYear - startYear;
    const firstValue = baseYearEmissions;
    const decrement = (firstValue - netZero) / totalYears;

    let resultData = [baseYearEmissions];

    for (let i = 0; i < totalYears; i++) {
      let value = resultData[resultData.length - 1] - decrement;
      resultData.push(value);
    }

    resultData[resultData.length - 1] = netZero;

    return resultData;
    /* } else {
      const initialLength = currentData?.length;
      const totalYears = endYear - startYear + 1;
      const remainingYears = totalYears - initialLength;

      // Calculate the decrement step to ensure the last value is 10
      const firstValue = currentData[currentData.length - 1];
      const decrement = (firstValue - netZero) / remainingYears;

      // Initialize the result array with the current data
      let resultData = [...currentData];

      // Generate data points from the next year after the initial data to endYear
      for (let i = 0; i < remainingYears; i++) {
        let value = resultData[resultData.length - 1] - decrement;
        resultData.push(value);
      }

      // Ensure the last value is exactly netZero (to account for floating point precision)
      resultData[resultData.length - 1] = netZero;

      return resultData;
    } */
  }

  //const predictedEmissions = predictEmissions(currentEmissionsData, startYear, endYear);
  const netZeroTarget =
    multiYearData && orgTarget?.useData
      ? calculateDataPoints(baseYearEmissions, orgTarget?.baseYear, orgTarget?.targetYear)
      : calculateDataPoints(currentEmissionsData, orgTarget?.baseYear, orgTarget?.targetYear);

  const options = {
    credits: false,
    chart: {
      type: 'areaspline',
      borderRadius: 15,
      spacing: [20, 20, 20, 20],
      height: 455,
    },
    title: {
      text: '',
      style: {
        fontSize: '0px',
        fontFamily: 'Open Sans',
        fontWeight: '600',
        color: '#130834',
      },
    },
    yAxis: {
      title: {
        text: 'Emissions (tCO2e)',
      },
      labels: {
        style: {
          fontSize: '0.9em',
        },
      },
      plotLines: [
        {
          color: '#FF0000',
          dashStyle: 'Dash',
          value: netZero, // Position of the line
          width: 2,
          label: {
            align: 'left',
            style: {
              color: '#FF0000',
              fontWeight: 'bold',
              fontSize: '13px',
            },
            text: 'Net Zero',
            x: 15,
          },
          zIndex: 10,
        },
      ],
    },
    xAxis: {
      accessibility: {
        rangeDescription: 'Years',
      },
      categories: Array.from({ length: orgTarget?.targetYear - orgTarget?.baseYear + 1 }, (_, i) =>
        (orgTarget?.baseYear + i).toString()
      ),
    },
    plotOptions: {
      series: {},
    },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
    },
    tooltip: {
      useHTML: true, // Enable HTML to use custom styling
      formatter: function () {
        // Return the formatted tooltip text
        return (
          `<span style="font-size: 12px;">Year: <b>${this.x}</b></span><br/>` +
          `<span style="font-size: 12px;"><span style="color:${this.color};">\u25CF</span> ${
            this.series.name
          }: <b>${this.y.toFixed(2)}</b></span>`
        );
      },
    },
    series: [
      {
        name: 'Actual Emissions',
        data: multiYearData && [...currentEmissionsData],
        zIndex: 5,
        lineWidth: 1,
        fillOpacity: 1,
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        color: palette?.[0] || '#1F9B38',
        type: 'areaspline',
      },
      {
        name: `Net Zero Target ${orgTarget?.targetYear}`,
        data: netZeroTarget,
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        zIndex: 4,
        fillOpacity: 0.2,
        lineWidth: 3,
        color: palette?.[1] || '#33C2EA',
        type: 'areaspline',
      },
      /* {
        name: 'Predicted Emissions',
        data: [...predictedEmissions],
        marker: {
          enabled: false,
          symbol: 'circle',
        },
        dashStyle: 'Dash',
        zIndex: 2,
        fillOpacity: 1,
        lineWidth: 3,
      }, */
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            chart: {
              height: 405,
            },
          },
        },
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  };

  const style = { transition, transform: CSS.Transform.toString(transform) };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '15px',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {editing && (
        <div className='chart-move' style={{ ...style, zIndex: 4 }} ref={setNodeRef} {...attributes} {...listeners}>
          <i className='fas fa-up-down-left-right move-icon' title='Move Chart'></i>
        </div>
      )}
      <div
        style={{
          marginTop: '10px',
          zIndex: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
        }}>
        <span
          style={{
            fontSize: '18px',
            fontFamily: 'Open Sans',
            fontWeight: '600',
            color: '#130834',
          }}>
          Net Zero Targets For {orgTargets?.length < 2 ? formatTargetCoverage(orgTarget?.targetCoverage) : ''}
        </span>
        <div style={{ display: orgTargets?.length > 1 ? 'flex' : 'none', alignItems: 'center' }}>
          {/* <Select
            styles={{
              container: (provided) => ({
                ...provided,
                float: 'right',
                marginLeft: 0,
                paddingBottom: 0,
              }),
              control: (base) => ({
                ...base,
                height: '35.69px',
                minHeight: 30,
                width: 180,
                borderColor: 'white',
                fontWeight: '600',
                fontSize: '17px',
                boxShadow: 0, // Customize focus border color
                '&:hover': {
                  borderColor: 'white', // Customize hover border color
                },
                fontFamily: 'inherit',
              }),
              placeholder: (defaultStyles) => {
                return {
                  ...defaultStyles,
                  fontFamily: 'inherit',
                };
              },
              indicatorsContainer: (provided) => ({
                ...provided,
                padding: 0, // Removes the space between the value and arrow
                borderColor: 'white',
              }),
              indicatorSeparator: (provided) => ({
                ...provided,
                display: 'none',
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                padding: 0,
              }),
              valueContainer: (provided, state) => ({
                ...provided,
                padding: '2px 5px',
              }),
            }}
            options={targetOptions}
            isSearchable={false}
            onChange={(selected) => {
              setOrgTarget(orgTargets?.find((o) => o?.id === selected?.value));
            }}
            value={targetOptions?.find((option) => option.value === orgTarget?.id)}
          /> */}
          <span style={{ fontSize: '18px', fontWeight: 600, color: '#130834', marginLeft: 5 }}>
            {formatTargetCoverage(orgTarget?.targetCoverage)}
          </span>
          <i
            id='target-select-icon'
            className='fas fa-chevron-down'
            onClick={(event) => {
              setPopoverOpen((open) => !open);
              setAnchorEl(event.currentTarget);
            }}
            style={{ marginLeft: '5px', color: '#130834' }}></i>
          <Popover
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={() => setPopoverOpen(false)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            disableScrollLock={true}
            slotProps={{
              paper: {
                style: {
                  marginTop: '10px',
                  borderRadius: 6,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 10,
                },
              },
            }}>
            {targetOptions.map((option) => (
              <li
                key={option.value}
                className={'netzero-popover-option' + (orgTarget?.id === option.value ? ' selected' : '')}
                onClick={() => {
                  setOrgTarget(orgTargets.find((o) => o.id === option.value));
                  setPopoverOpen(false);
                }}>
                {option.label}
              </li>
            ))}
          </Popover>
        </div>
      </div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    multiYearData: state.dashboard?.data?.multiYear,
    branding: state?.branding?.colors || {},
  };
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(NetZeroChart);
